import * as z from 'zod/mini'

/**
 * Every validation message Seiri writes itself, by key, in English. See docs/api-errors.md.
 *
 * Schemas never carry a sentence in any language: a Seiri rule fails with a custom issue whose
 * `params.key` is one of these keys and which has no `message`. Zod then resolves the text through
 * the global config, so each app decides the language - the frontends translate the key
 * (`configureZod` in `@seiri/shared/frontend`), the API reads the English here when it formats a 400.
 *
 * Zod's built-in checks (`z.minLength(2)`, `z.email()`) keep their built-in codes and are translated
 * by Zod's own locale. A key is only for a rule Zod has no wording for.
 */
export const VALIDATION_MESSAGES_EN = {
  'validation.required': 'Required',
  'validation.endAfterStart': 'End must be after start',
  'validation.startInPast': 'Start cannot be in the past',
  'validation.beyondPlanningHorizon': 'A service can run at most 6 months ahead',
  'validation.timeFormat': 'Time must be in the HH:mm format',
  'validation.openingHoursOverlap': 'Intervals must not overlap',
  'validation.timezone': 'Invalid time zone',
  'validation.ageRange': 'Invalid age range',
  'validation.passwordStrength':
    'Password must have at least 8 characters and combine 3 of 4: lowercase, uppercase, digit, special character',
  'validation.passwordsMatch': 'Passwords must match',
  'validation.phoneCharacters': 'Phone may contain only digits, spaces and the characters + / -',
  'validation.phoneMinLength': 'Phone must have at least 4 characters',
  'validation.consentRequired': 'Consent is required',
  'validation.emailsRequired': 'Add at least one email',
  'validation.clientsRequired': 'Choose at least one client',
  'validation.activityRequired': 'Select an activity',
  'validation.placeRequired': 'Select a place',
  'validation.colorRequired': 'Select a colour',
  'validation.placesRequired': 'Select places or "No place"',
  'validation.employeesRequired': 'Select employees or "No employee"',
  'validation.intervalsRequired': 'Add at least one time interval',
  'validation.weekdaysRequired': 'Select at least one day',
  'validation.weeksRequired': 'Select at least one week',
  'validation.reoccurEndRequired': 'Set either an end date or a number of repetitions',
  'validation.reoccurCountRequired': 'Set the number of repetitions',
  'validation.reoccurUntilRequired': 'Set the date the repetition ends',
  'validation.reoccurUntilAfterStart': 'The repetition must end after it starts',
  'validation.blueprintOrActivityRequired': 'blueprintId or activityId is required',
  'validation.slotRangeTooLong': 'The window may cover at most 31 days',
  'validation.eventRangeTooLong': 'The window may cover at most 92 days',
  'validation.eventOrCustomerRequired': 'eventId or customerId is required',
  'validation.mjmlXorHtml': 'Provide either MJML or HTML content, not both',
  'validation.httpUrl': 'Enter a full web address starting with http:// or https://',
  'validation.duplicateOrigin': 'This website is already in the list',
  'validation.galleryOrder': 'List every image of the gallery exactly once',
} as const satisfies Record<`validation.${string}`, string>

export type ValidationKey = keyof typeof VALIDATION_MESSAGES_EN

export const isValidationKey = (value: unknown): value is ValidationKey =>
  typeof value === 'string' && Object.hasOwn(VALIDATION_MESSAGES_EN, value)

/** The key a Seiri rule put on an issue, or `undefined` for one of Zod's built-in issues. */
export const validationKeyOf = (issue: { readonly params?: unknown }): ValidationKey | undefined => {
  const key = (issue.params as { key?: unknown } | undefined)?.key
  return isValidationKey(key) ? key : undefined
}

/** A check that fails with `key`: `z.string().check(refineWithKey('validation.timezone', isZone))`. */
export const refineWithKey = <T>(key: ValidationKey, predicate: (value: T) => boolean) =>
  z.refine<T>(predicate, { params: { key } })

/** The issue a `superRefine` adds for a Seiri rule: `ctx.addIssue(keyedIssue('validation.ageRange', ['ageEnd']))`. */
export const keyedIssue = (key: ValidationKey, path: PropertyKey[]) => ({
  code: 'custom' as const,
  path,
  params: { key },
})
